import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import BubbleTail from "@app/neva/components/bubbleTail";
import { EventsContext, NevaContext } from "@app/neva/provider";
import { EventDataMessageType } from "@app/neva/models";
import { VideoType } from "@app/neva/models/apiControllerModel";
import { IVideo } from "@app/components/videoPlayer";
import "./videosMessage.scss";
import VideoPlayerWrapper from "@app/neva/components/message/videoPlayerWrapper/videoPlayerWrapper";

interface Props {
  message: EventDataMessageType;
  isShowAvatar: boolean;
  isLastMessage: boolean;
  scrollToBottom: () => void;
}

const TIMEOUT_DELAY = 1000;

const VideosMessage: FC<Props> = ({
  message,
  isShowAvatar,
  isLastMessage,
  scrollToBottom,
}) => {
  const { isWidgetOpen, hasVideoRendered, setHasVideoRendered } =
    useContext(NevaContext);
  const {
    eventsProviderState: { isAllVideosRendered },
    setIsAllVideosRendered,
  } = useContext(EventsContext);
  const [visibleVideos, setVisibleVideos] = useState<VideoType[]>([]);
  const videoInstancesRef = useRef<Map<string, IVideo>>(new Map());

  const registerVideo = useCallback((videoId: string, instance: IVideo) => {
    videoInstancesRef.current.set(videoId, instance);
  }, []);

  const unregisterVideo = useCallback((videoId: string) => {
    videoInstancesRef.current.delete(videoId);
  }, []);

  const handleVideoPlay = useCallback((playingId: string) => {
    videoInstancesRef.current.forEach((instance, id) => {
      if (id !== playingId) {
        (instance as IVideo & { pause?: () => void }).pause?.();
      }
    });
  }, []);

  useEffect(() => {
    let timeouts: NodeJS.Timeout[];

    if (isWidgetOpen && message.videos) {
      if (hasVideoRendered.current) {
        setVisibleVideos(message.videos);
      } else {
        timeouts = message.videos.map((video, index) => {
          return setTimeout(() => {
            setVisibleVideos((prevState) => [...prevState, video]);
          }, index * TIMEOUT_DELAY);
        });
        setHasVideoRendered(true);
      }
    }

    return () => {
      timeouts && timeouts.forEach(clearTimeout);
    };
  }, [isWidgetOpen, message.videos]);

  useEffect(() => {
    const messageVideosLength = message.videos?.length ?? 0;
    if (messageVideosLength === visibleVideos.length) {
      setIsAllVideosRendered(true);
    }
    scrollToBottom();
  }, [visibleVideos, isAllVideosRendered]);

  return (
    <div className="video-message__wrapper">
      <ul className="video-message__videos-list">
        {!!visibleVideos.length &&
          visibleVideos.map((video, index) => (
            <li key={video.videoId} className="video-message__list-item">
              <span className="video-message__list-item-title">
                {video.title}
              </span>
              <VideoPlayerWrapper
                video={video}
                index={index}
                isLatestVideo={
                  isLastMessage && index === (message.videos?.length ?? 0) - 1
                }
                registerVideo={registerVideo}
                unregisterVideo={unregisterVideo}
                onVideoPlay={handleVideoPlay}
              />
            </li>
          ))}
      </ul>
      {(isShowAvatar || !!message.isSendByUser) && (
        <BubbleTail
          isShowAvatar={isShowAvatar}
          isSendByUser={!!message.isSendByUser}
        />
      )}
    </div>
  );
};

export default VideosMessage;
